/**
 * @genesis_domain Auth Engine
 * @genesis_version V9.1
 * @genesis_intent Authentication service for user registration, login, logout, and password recovery
 * @genesis_boundaries No UI logic, no routing
 * @genesis_ai_trail V90.0: Migrated from @/services/AuthService.ts to @/domains/auth/services/authService.ts as part of Operation Beta DDD realignment.
 * @genesis_ai_trail V9.0: Added sendResetPasswordEmail and updateUserPassword for Supabase PKCE password reset loop.
 */

import { queryClient } from '@/core/api/queryClient.js';
import { supabase } from '@/core/api/supabase';
import type { AuthRegister } from '@shared/schemas';

interface User {
    id: string;
    email: string;
    user_metadata?: {
        name?: string;
    };
}

interface Session {
    access_token: string;
    refresh_token?: string;
    expires_at?: number;
}

interface AuthResponse {
    user: User;
    session: Session | null;
}

/**
 * Register a new user
 */
export const register = async (credentials: AuthRegister): Promise<AuthResponse> => {
    const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
            data: {
                name: credentials.name,
            },
        },
    });

    if (error) throw error;

    if (!data.user) {
        throw new Error('Registration succeeded but no user returned');
    }

    // If email confirmations are enabled, Supabase returns data.user but data.session is null.
    // This is not an error — the user must verify their email before they can log in.
    if (!data.session) {
        return {
            user: {
                id: data.user.id,
                email: data.user.email ?? '',
                user_metadata: data.user.user_metadata as { name?: string } | undefined,
            },
            session: null,
        };
    }

    return {
        user: {
            id: data.user.id,
            email: data.user.email ?? '',
            user_metadata: data.user.user_metadata as { name?: string } | undefined,
        },
        session: {
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
            expires_at: data.session.expires_at,
        },
    };
};

/**
 * Logout user - V7.4.8 Safe Logout Flow
 * CRITICAL: Let Supabase manage its own storage cleanup
 */
export const logout = async (): Promise<void> => {
    performance.mark('logout-start');

    // Step 1: Synchronous Supabase signOut - let it manage its own storage cleanup
    performance.mark('signout-start');
    try {
        await supabase.auth.signOut();
    } catch {
        // Ignore errors - Supabase will handle cleanup
    }
    performance.mark('signout-end');
    performance.measure('signout-duration', 'signout-start', 'signout-end');


    // Step 3: Safely clear TanStack Query cache (protect auth key)
    queryClient.invalidateQueries();
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });

    // Step 4: Cleanup complete
    performance.mark('logout-end');
    performance.measure('logout-total-duration', 'logout-start', 'logout-end');

    // Navigation should be handled by the caller (e.g., AuthContext using useNavigate)
};

/**
 * Send a password reset email to the user via Supabase Auth.
 * Uses PKCE flowType configured in the Supabase client.
 * The redirectTo URL is where the user lands after clicking the email link.
 */
export const sendResetPasswordEmail = async (email: string): Promise<void> => {
    const redirectTo = `${window.location.origin}/auth/callback?next=/update-password`;


    const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
    });

    if (error) throw error;
};

/**
 * Update the authenticated user's password.
 * This is called from the password reset page after the user clicks the email link
 * and Supabase has established a temporary session via PKCE.
 */
export const updateUserPassword = async (password: string): Promise<void> => {
    const { error } = await supabase.auth.updateUser({ password });

    if (error) throw error;
};

/**
 * Verify an 8-digit OTP code sent via password reset email.
 * This provides an alternative to clicking the recovery link,
 * avoiding link scanner consumption issues (e.g., Yahoo Mail).
 */
export const verifyRecoveryOtp = async (email: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'recovery',
    });
    if (error) throw error;
    return data;
};

/**
 * Verify an 8-digit OTP code for email signup confirmation.
 * Called after user registration when email confirmations are enabled.
 * The token is the 8-digit code sent to the user's email.
 */
export const verifySignupOtp = async (email: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'signup',
    });
    if (error) throw error;
    return data;
};
